import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, CheckCircle2, Clock, AlertCircle, CreditCard, Sparkles, Pencil } from "lucide-react";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payingId, setPayingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/my/deals");
      setDeals(data.items);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const subscribe = async (deal_id) => {
    setPayingId(deal_id);
    try {
      const { data } = await api.post("/payments/checkout", {
        deal_id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start checkout");
      setPayingId(null);
    }
  };

  const featureDeal = async (deal_id) => {
    if (!window.confirm("Feature this deal at the top of search results for 7 days for $19?")) return;
    setPayingId(deal_id);
    try {
      const { data } = await api.post("/payments/feature-checkout", {
        deal_id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start feature checkout");
      setPayingId(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this deal?")) return;
    await api.delete(`/my/deals/${id}`);
    toast.success("Deal deleted");
    load();
  };

  const planLabel = user?.role === "business" ? "$99.99 / deal / month" : "$49.99 / deal / month";

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#5F6C60]">Dashboard</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl tracking-tight mt-2">Hi, {user?.name}</h1>
          <p className="text-[#5F6C60] mt-2">Your plan: <span className="text-[#2C352D] font-medium">{planLabel}</span></p>
        </div>
        <Button data-testid="new-deal-button" onClick={()=>navigate("/dashboard/new")} className="rounded-full bg-[#D97757] hover:bg-[#C26243] text-white px-6 h-12">
          <Plus className="w-4 h-4 mr-2" /> New deal
        </Button>
      </div>

      <div className="bg-[#0F766E]/8 border border-[#0F766E]/25 rounded-2xl p-5 mb-8 text-sm text-[#2C352D]" data-testid="trial-banner">
        <strong className="text-[#0F766E]">How it works:</strong> submit a deal → admin approves within 24h → it goes live free for <strong>3 days</strong> → after that, subscribe ({planLabel}) to keep it published for another 30 days.
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
        <StatCard label="Live (trial + active)" value={deals.filter(d=>isLive(d)).length} icon={CheckCircle2} />
        <StatCard label="Awaiting review" value={deals.filter(d=>d.status==="pending_review").length} icon={Clock} />
        <StatCard label="Needs your attention" value={deals.filter(d=>needsAction(d)).length} icon={AlertCircle} />
      </div>

      <div className="bg-white rounded-2xl border border-[#E8E2D6] overflow-hidden">
        <div className="p-6 border-b border-[#E8E2D6]">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#5F6C60]">Your deals</div>
        </div>
        {loading ? (
          <div className="p-10 text-center text-[#5F6C60]">Loading…</div>
        ) : deals.length === 0 ? (
          <div className="p-16 text-center">
            <p className="font-serif-display text-2xl">No deals yet</p>
            <p className="text-[#5F6C60] mt-2">Create your first deal — it'll go live as soon as you subscribe.</p>
            <Link to="/dashboard/new"><Button data-testid="empty-create-deal" className="mt-6 rounded-full bg-[#D97757] hover:bg-[#C26243] text-white px-6">Create deal</Button></Link>
          </div>
        ) : (
          <ul className="divide-y divide-[#E8E2D6]">
            {deals.map(d => (
              <li key={d.id} data-testid={`my-deal-${d.id}`} className="p-6 flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="bg-[#0F766E]/10 text-[#0F766E] text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">{d.category}</span>
                    <StatusBadge status={d.status} until={d.trial_until || d.active_until} permaFree={d.perma_free} />
                    {d.featured_until && new Date(d.featured_until) > new Date() && (
                      <span data-testid={`featured-badge-${d.id}`} className="bg-[#D97757]/15 text-[#C26243] text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full inline-flex items-center gap-1">
                        <Sparkles className="w-3 h-3" strokeWidth={1.5} /> Featured · until {new Date(d.featured_until).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  <div className="font-serif-display text-xl">{d.title}</div>
                  <div className="text-sm text-[#5F6C60]">{d.city}, {d.state} · {d.duration_minutes} min · ${d.deal_price.toFixed(0)} <span className="line-through ml-1">${d.original_price.toFixed(0)}</span></div>
                  {d.rejection_reason && <div className="text-xs text-[#9D5C5C] mt-1">Admin note: {d.rejection_reason}</div>}
                </div>
                <div className="flex items-center gap-2">
                  {showSubscribe(d) && (
                    <Button data-testid={`publish-${d.id}`} onClick={()=>subscribe(d.id)} disabled={payingId===d.id} className="rounded-full bg-[#D97757] hover:bg-[#C26243] text-white">
                      <CreditCard className="w-4 h-4 mr-2" /> {payingId===d.id ? "Redirecting…" : "Subscribe to relist"}
                    </Button>
                  )}
                  {isLive(d) && (
                    <>
                      <Button data-testid={`feature-${d.id}`} onClick={()=>featureDeal(d.id)} disabled={payingId===d.id} variant="outline" className="rounded-full text-[#0F766E] border-[#0F766E]/40 hover:bg-[#0F766E]/10">
                        <Sparkles className="w-4 h-4 mr-2" strokeWidth={1.5} /> Feature 7d · $19
                      </Button>
                      <Link to={`/deals/${d.id}`}><Button variant="outline" className="rounded-full">View live</Button></Link>
                    </>
                  )}
                  <Link to={`/dashboard/edit/${d.id}`}>
                    <Button data-testid={`edit-${d.id}`} variant="outline" className="rounded-full">
                      <Pencil className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Edit
                    </Button>
                  </Link>
                  <Button data-testid={`delete-${d.id}`} variant="ghost" onClick={()=>remove(d.id)} className="rounded-full text-[#9D5C5C] hover:bg-[#9D5C5C]/10">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="bg-white rounded-2xl border border-[#E8E2D6] p-6">
      <div className="flex items-center justify-between">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#5F6C60]">{label}</div>
        <Icon className="w-4 h-4 text-[#D97757]" strokeWidth={1.5} />
      </div>
      <div className="font-serif-display text-4xl mt-3">{value}</div>
    </div>
  );
}

function StatusBadge({ status, until, permaFree }) {
  const now = new Date();
  if (permaFree) return <span className="bg-[#0F766E] text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">Active · permanently free</span>;
  if (status === "trial" && until && new Date(until) > now) return <span className="bg-[#0F766E]/10 text-[#0F766E] text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">Free trial · until {new Date(until).toLocaleDateString()}</span>;
  if (status === "active" && until && new Date(until) > now) return <span className="bg-[#0F766E]/10 text-[#0F766E] text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">Active · until {new Date(until).toLocaleDateString()}</span>;
  if (status === "trial" || status === "active") return <span className="bg-[#D97757]/15 text-[#C26243] text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">Window ended · subscribe to relist</span>;
  if (status === "pending_review") return <span className="bg-[#D4A373]/20 text-[#8b6a3f] text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full inline-flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Awaiting admin review</span>;
  if (status === "rejected") return <span className="bg-[#9D5C5C]/15 text-[#9D5C5C] text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">Rejected</span>;
  if (status === "suspended") return <span className="bg-[#5F6C60]/20 text-[#2C352D] text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">Suspended</span>;
  return <span className="bg-[#E8E2D6] text-[#5F6C60] text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">{status}</span>;
}

function isLive(d) {
  const now = new Date();
  if (d.status === "trial" && d.trial_until && new Date(d.trial_until) > now) return true;
  if (d.status === "active" && d.active_until && new Date(d.active_until) > now) return true;
  return false;
}

function showSubscribe(d) {
  const now = new Date();
  if (d.status === "trial" && (!d.trial_until || new Date(d.trial_until) <= now)) return true;
  if (d.status === "active" && (!d.active_until || new Date(d.active_until) <= now)) return true;
  return false;
}

function needsAction(d) {
  return showSubscribe(d) || d.status === "rejected" || d.status === "suspended";
}
